'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getSearchSuggestions } from '@maestro/commerce-blocks/client/api/search'
import { useLastSearches } from '@maestro/commerce-blocks/components/search-bar/batons/use-last-searches'
import { useSearchContext } from '@maestro/commerce-blocks/components/search-bar/search.context'
import { useDebouncedCancellableCallback } from '@maestro/commerce-blocks/hooks/use-debounced-cancellable-callback'
import { usePrevious } from '@maestro/commerce-blocks/hooks/use-previous'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { createLogger } from '@maestro/core/logger'
import {
  EventType,
  type SearchSuggestion,
  TopNavigationTitle,
} from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'
import { useClickOutsideElement } from '@pearlconvert/ui/hooks/use-click-outside-element'
import { useBreakpoint } from '@pearlconvert/ui/providers/breakpoint/breakpoint.context'
import { useDisabledPageScroll } from '@pearlconvert/ui/providers/disabled-page-scroll/disabled-page-scroll.context'

const log = createLogger('maestro:commerce-blocks:search-bar')
const searchPanelId = 'searchbar-suggestions-panel'

export const useSearchBar = () => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const { isDesktopView, isMobileView } = useBreakpoint()
  const { toggleDisabledPageScroll } = useDisabledPageScroll()
  const { currentSearchData, hasNoSearchResults, clearSearchData } =
    useSearchContext()
  const { publish } = useEventBusContext()

  const { lastSearches, addSearch, removeSearch } = useLastSearches()
  const { fetchSuggestionsDelay, searchDelay } = theme().search.searchBar
  const { urls } = theme()

  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLElement>(null)
  const isNavigatingToSearchRef = useRef(false)
  const isSubmittedRef = useRef(false)
  const hasFetchedPopularSearchesRef = useRef(false)
  const pathnamePrev = usePrevious('searchBarPathnamePrev', pathname, false)
  const suggestionsRequestIdRef = useRef(0)

  const [originPath, setOriginPath] = useState('/')
  const [initSearch, setInitSearch] = useState(false)
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const [input, setInput] = useState(searchParams.get('text') || '')
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  const [popularSearches, setPopularSearches] = useState<SearchSuggestion[]>([])
  const [activeIndex, setActiveIndex] = useState(-1)
  const [activeOptionId, setActiveOptionId] = useState<string>()
  const [removalMessage, setRemovalMessage] = useState('')
  const [isLoadingPopularSearches, setIsLoadingPopularSearches] = useState(true)

  const resetActiveOption = useCallback(() => {
    setActiveIndex(-1)
    setActiveOptionId(undefined)
  }, [])

  const getSearchOptions = useCallback(() => {
    return wrapperRef.current?.querySelectorAll<HTMLElement>('[role="option"]')
  }, [])

  const closeSearch = useCallback(
    ({
      restoreInputFocus = false,
      shouldAddCurrentValue = false,
    }: {
      restoreInputFocus?: boolean
      shouldAddCurrentValue?: boolean
    } = {}) => {
      if (!isSearchVisible || isNavigatingToSearchRef.current) {
        return
      }

      if (shouldAddCurrentValue) {
        const currentValue = inputRef.current?.value || ''
        if (currentValue) {
          addSearch(currentValue)
        }
      }

      setIsSearchVisible(false)
      resetActiveOption()
      toggleDisabledPageScroll(false)

      if (restoreInputFocus) {
        inputRef.current?.focus()
      }
    },
    [addSearch, isSearchVisible, resetActiveOption, toggleDisabledPageScroll]
  )

  useClickOutsideElement(wrapperRef, () => {
    if (isSearchVisible) {
      closeSearch({ shouldAddCurrentValue: true })
    }
  })

  const hideSearch = useCallback(() => {
    if (!isSearchVisible || isNavigatingToSearchRef.current) {
      return
    }
    closeSearch()
  }, [closeSearch, isSearchVisible])

  // Live search effect
  useEffect(() => {
    if (pathname !== urls.search && searchParams) {
      setOriginPath(`${pathname}?${searchParams}`)
    }

    if (pathname === urls.search) {
      if (initSearch && input === '') {
        router.push(originPath)
      } else if (
        !isSearchVisible &&
        document.activeElement !== inputRef.current
      ) {
        setInput(searchParams.get('text') || '')
      }
    }
  }, [
    initSearch,
    input,
    originPath,
    pathname,
    router,
    searchParams,
    isSearchVisible,
    urls.search,
  ])

  // Clear search bar on navigation changes
  useEffect(() => {
    setInitSearch(false)
    if (pathname !== urls.search) {
      setInput('')
    }
  }, [pathname, searchParams, urls.search])

  useEffect(() => {
    if (
      pathnamePrev !== pathname &&
      isSearchVisible &&
      pathname !== urls.search &&
      isMobileView()
    ) {
      closeSearch()
    }
  }, [
    closeSearch,
    isMobileView,
    isSearchVisible,
    pathname,
    pathnamePrev,
    urls.search,
  ])

  useEffect(() => {
    if (!isSearchVisible) {
      resetActiveOption()
      return
    }

    const searchOptions = getSearchOptions()

    if (!searchOptions?.length || activeIndex < 0) {
      setActiveOptionId(undefined)
      return
    }

    if (activeIndex >= searchOptions.length) {
      setActiveIndex(searchOptions.length - 1)
      return
    }

    setActiveOptionId(searchOptions[activeIndex]?.id)
  }, [
    activeIndex,
    getSearchOptions,
    isSearchVisible,
    lastSearches.length,
    popularSearches.length,
    resetActiveOption,
    suggestions.length,
  ])

  useEffect(() => {
    const wrapper = wrapperRef.current
    const handleScroll = () => {
      // On mobile the search is a fullscreen overlay with page scroll disabled,
      // and iOS fires a scroll event when the virtual keyboard opens right after
      // focus — closing the dropdown immediately. Skip scroll-to-close on mobile.
      if (isMobileView()) {
        return
      }
      hideSearch()
    }

    // Update available space when the virtual keyboard toggles
    function handleResize() {
      wrapperRef.current?.style.setProperty(
        '--searchbar__root-available-height',
        window?.visualViewport?.height + 'px'
      )
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Close search dropdown on Escape
      if (event.key === 'Escape' && isSearchVisible) {
        event.preventDefault()
        closeSearch({ restoreInputFocus: true })
        return
      }
    }

    // Close dropdown when Tab moves focus outside search area
    const handleFocusOut = () => {
      if (!isSearchVisible) {
        return
      }

      // Use setTimeout to allow focus to settle on the new element
      setTimeout(() => {
        if (isNavigatingToSearchRef.current) {
          return
        }
        const activeElement = document.activeElement
        const isInsideSearch = wrapper?.contains(activeElement as Node)

        if (!isInsideSearch) {
          closeSearch({ shouldAddCurrentValue: true })
        }
      }, 0)
    }

    window.addEventListener('scroll', handleScroll)
    window.visualViewport?.addEventListener('resize', handleResize)
    wrapper?.addEventListener('keydown', handleKeyDown)
    wrapper?.addEventListener('focusout', handleFocusOut)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.visualViewport?.removeEventListener('resize', handleResize)
      wrapper?.removeEventListener('keydown', handleKeyDown)
      wrapper?.removeEventListener('focusout', handleFocusOut)
    }
  }, [closeSearch, hideSearch, isMobileView, isSearchVisible])

  useEffect(() => {
    if (isMobileView() && isSearchVisible) {
      toggleDisabledPageScroll(true)
    }
  }, [isSearchVisible, isMobileView, toggleDisabledPageScroll])

  const { invoke: debouncedFetchSuggestions, cancel: cancelFetchSuggestions } =
    useDebouncedCancellableCallback(async (search: string) => {
      const requestId = ++suggestionsRequestIdRef.current

      if (search.length < 2) {
        setSuggestions([])
        return
      }

      try {
        const data = await getSearchSuggestions(search)
        if (requestId === suggestionsRequestIdRef.current) {
          setSuggestions(data.suggestions || [])
        }
      } catch (error) {
        log.error('Error fetching suggestions:', error)
        if (requestId === suggestionsRequestIdRef.current) {
          setSuggestions([])
        }
      }
    }, fetchSuggestionsDelay)

  const { invoke: debouncedSearch, cancel: cancelDebouncedSearch } =
    useDebouncedCancellableCallback((search: string) => {
      if (search.length < 2) {
        return
      }
      if (pathname !== urls.search) {
        isNavigatingToSearchRef.current = true
      }
      router.push(`/search?text=${encodeURIComponent(search)}`)
    }, searchDelay)

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      setInitSearch(true)
      setInput(newValue)
      resetActiveOption()

      if (newValue.length < 2) {
        clearSearchData()
      }

      debouncedFetchSuggestions(newValue)
      debouncedSearch(newValue)
    },
    [
      clearSearchData,
      debouncedFetchSuggestions,
      debouncedSearch,
      resetActiveOption,
    ]
  )

  const handleFocusInput = useCallback(() => {
    if (isNavigatingToSearchRef.current) {
      return
    }
    setIsSearchVisible(true)
    resetActiveOption()
  }, [resetActiveOption])

  const handleBlurInput = useCallback(() => {
    if (isNavigatingToSearchRef.current && !isSubmittedRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
    isSubmittedRef.current = false
  }, [])

  const handleKeyDownInput = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        setIsSearchVisible(true)

        const searchOptions = getSearchOptions()
        const optionsCount = searchOptions?.length || 0

        if (!optionsCount) {
          return
        }

        if (e.key === 'ArrowDown') {
          setActiveIndex((prevIndex) =>
            prevIndex < 0 ? 0 : (prevIndex + 1) % optionsCount
          )
          return
        }

        setActiveIndex((prevIndex) =>
          prevIndex < 0
            ? optionsCount - 1
            : (prevIndex - 1 + optionsCount) % optionsCount
        )
        return
      }

      if (e.key === 'Enter' && activeIndex >= 0 && isSearchVisible) {
        const searchOptions = getSearchOptions()
        const activeOption = searchOptions?.[activeIndex]
        const interactiveElement = activeOption?.querySelector<HTMLElement>(
          'a, button:not([type="submit"])'
        )

        if (interactiveElement) {
          e.preventDefault()
          interactiveElement.click()
        }
        return
      }

      if (e.key === 'Escape' && isSearchVisible) {
        e.preventDefault()
        closeSearch({ restoreInputFocus: true })
      }
    },
    [activeIndex, closeSearch, getSearchOptions, isSearchVisible]
  )

  useEffect(() => {
    const fetchPopularSearches = async () => {
      try {
        const data = await getSearchSuggestions()
        setPopularSearches(data.suggestions || [])
      } catch (error) {
        log.error('Error fetching popular searches:', error)
      } finally {
        setIsLoadingPopularSearches(false)
      }
    }

    if (isSearchVisible && !hasFetchedPopularSearchesRef.current) {
      hasFetchedPopularSearchesRef.current = true
      fetchPopularSearches()
    }
  }, [isSearchVisible])

  // Clear navigation ref after reaching the search page
  useEffect(() => {
    if (isNavigatingToSearchRef.current && pathname === urls.search) {
      const timer = setTimeout(() => {
        isNavigatingToSearchRef.current = false
      }, 150)
      return () => {
        clearTimeout(timer)
        isNavigatingToSearchRef.current = false
      }
    }
  }, [pathname, urls.search])

  const handleSuggestionSelectAction = useCallback(
    (query: string) => {
      setInput(query)
      closeSearch()
    },
    [closeSearch]
  )

  const resetSearch = useCallback(
    (shouldAddSearch = false) => {
      setInitSearch(true)
      setInput('')
      setIsSearchVisible(false)
      resetActiveOption()
      toggleDisabledPageScroll(false)

      const inputEl = inputRef.current
      const value = inputEl?.value

      if (shouldAddSearch && value) {
        addSearch(value)
        inputEl.focus()
      }
    },
    [addSearch, resetActiveOption, toggleDisabledPageScroll]
  )

  const handleSubmitSearch = useCallback(
    (e: React.SubmitEvent) => {
      e.preventDefault()
      const currentValue = inputRef.current?.value || ''
      if (!currentValue) {
        return
      }

      addSearch(currentValue)
      cancelDebouncedSearch()
      cancelFetchSuggestions()
      suggestionsRequestIdRef.current++

      if (pathname !== urls.search) {
        isNavigatingToSearchRef.current = true
      }
      isSubmittedRef.current = true

      // Close the panel directly — hideSearch/closeSearch both guard on isNavigatingToSearchRef,
      // so they would no-op here after we set it above.
      setIsSearchVisible(false)
      resetActiveOption()
      toggleDisabledPageScroll(false)
      inputRef.current?.blur()

      router.push(`/search?text=${encodeURIComponent(currentValue)}`)

      publish(EventType.TOP_NAVIGATION_CLICKED, {
        itemClicked: TopNavigationTitle.SEARCH,
      })
    },
    [
      addSearch,
      cancelFetchSuggestions,
      cancelDebouncedSearch,
      pathname,
      publish,
      resetActiveOption,
      router,
      toggleDisabledPageScroll,
      urls.search,
    ]
  )

  const isSuggestionsPanelOpen = useMemo(() => {
    return (
      isSearchVisible &&
      !hasNoSearchResults &&
      (input.length < 2 ||
        !!suggestions.length ||
        (isMobileView() && !!currentSearchData?.resultCount))
    )
  }, [
    currentSearchData?.resultCount,
    hasNoSearchResults,
    input.length,
    isMobileView,
    isSearchVisible,
    suggestions.length,
  ])

  return {
    handleBlurInput,
    handleInputChange,
    handleFocusInput,
    handleKeyDownInput,
    handleSubmitSearch,
    handleSuggestionSelectAction,
    hasNoSearchResults,
    hideSearch,
    activeOptionId,
    input,
    inputRef,
    isDesktopView,
    isInputTooShort: input.length < 2,
    isLoadingPopularSearches,
    isMobileView,
    isSearchVisible,
    isSuggestionsPanelOpen,
    lastSearches,
    popularSearches,
    productsCount: currentSearchData?.resultCount || 0,
    removalMessage,
    removeSearch,
    resetSearch,
    searchResult: currentSearchData,
    searchPanelId,
    setIsSearchVisible,
    setRemovalMessage,
    suggestions,
    wrapperRef,
  }
}
